import { FormEvent, useState } from 'react';

import { Plus, Trash2 } from 'lucide-react';
import {
  Box,
  Button,
  CssBaseline,
  MenuItem,
  TextField,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import previewTheme from './previewTheme';

type ExperimentType = 'single' | 'trio' | 'custom';
type SampleType = 'bam' | 'unpaired_fastq' | 'paired_fastq';
type Gender = 'female' | 'male' | 'unknown';

interface SampleDraft {
  id: string;
  sampleId: string;
  sampleType: SampleType;
  gender: Gender;
  filePath: string;
}

interface ExperimentDraft {
  id: string;
  name: string;
  description: string;
  type: ExperimentType;
  pipeline: string;
  samples: SampleDraft[];
}

const PIPELINES = ['Germline whole genome', 'Somatic panel', 'RNA sequencing'];

const TYPE_LABEL: Record<ExperimentType, string> = {
  single: 'Single',
  trio: 'Trio',
  custom: 'Custom',
};

const createId = () => crypto.randomUUID();

const createSample = (): SampleDraft => ({
  id: createId(),
  sampleId: '',
  sampleType: 'bam',
  gender: 'unknown',
  filePath: '',
});

const samplesForType = (
  type: ExperimentType,
  current: SampleDraft[],
): SampleDraft[] => {
  if (type === 'single') {
    return current.slice(0, 1).length > 0
      ? current.slice(0, 1)
      : [createSample()];
  }

  if (type === 'trio') {
    const next = current.slice(0, 3);
    while (next.length < 3) next.push(createSample());
    return next;
  }

  return current.length > 0 ? current : [createSample()];
};

const createExperiment = (): ExperimentDraft => ({
  id: createId(),
  name: '',
  description: '',
  type: 'single',
  pipeline: '',
  samples: [createSample()],
});

const fieldGrid = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  gap: 2,
};

const ProjectPreview = () => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [experiments, setExperiments] = useState<ExperimentDraft[]>([
    createExperiment(),
  ]);
  const [notice, setNotice] = useState('');

  const updateExperiment = (
    id: string,
    patch: Partial<Omit<ExperimentDraft, 'id' | 'samples'>>,
  ) => {
    setExperiments((current) =>
      current.map((experiment) =>
        experiment.id === id ? { ...experiment, ...patch } : experiment,
      ),
    );
  };

  const changeExperimentType = (id: string, type: ExperimentType) => {
    setExperiments((current) =>
      current.map((experiment) =>
        experiment.id === id
          ? {
              ...experiment,
              type,
              samples: samplesForType(type, experiment.samples),
            }
          : experiment,
      ),
    );
  };

  const updateSample = (
    experimentId: string,
    sampleId: string,
    patch: Partial<Omit<SampleDraft, 'id'>>,
  ) => {
    setExperiments((current) =>
      current.map((experiment) =>
        experiment.id === experimentId
          ? {
              ...experiment,
              samples: experiment.samples.map((sample) =>
                sample.id === sampleId ? { ...sample, ...patch } : sample,
              ),
            }
          : experiment,
      ),
    );
  };

  const addSample = (experimentId: string) => {
    setExperiments((current) =>
      current.map((experiment) => {
        if (experiment.id !== experimentId) return experiment;
        return {
          ...experiment,
          type: 'custom',
          samples: [...experiment.samples, createSample()],
        };
      }),
    );
  };

  const removeSample = (experimentId: string, sampleId: string) => {
    setExperiments((current) =>
      current.map((experiment) => {
        if (experiment.id !== experimentId) return experiment;
        const samples = experiment.samples.filter(
          (sample) => sample.id !== sampleId,
        );
        return {
          ...experiment,
          type: samples.length === 3 ? experiment.type : 'custom',
          samples,
        };
      }),
    );
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNotice(
      'Preview only. Nothing was sent to the server, and the current project form is unchanged.',
    );
  };

  return (
    <ThemeProvider theme={previewTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
        }}
      >
        <Box
          component="header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            height: 56,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontWeight: 600, letterSpacing: '-0.02em' }}>
            Synapsys
          </Typography>
          <Typography variant="body2" color="text.secondary">
            UI preview
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{
            maxWidth: 920,
            mx: 'auto',
            px: { xs: 2, md: 3 },
            py: { xs: 3, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'flex-end' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Project
              </Typography>
              <Typography variant="h1" sx={{ mt: 0.5 }}>
                {name.trim() || 'New project'}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75 }}
              >
                Name the project, then add the experiments and samples that
                belong to it.
              </Typography>
            </Box>
            <Button type="submit" variant="contained">
              Save project
            </Button>
          </Box>

          {notice && (
            <Typography variant="body2" color="text.secondary">
              {notice}
            </Typography>
          )}

          <Box
            sx={{
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: { xs: 2, md: 3 },
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Details
            </Typography>
            <Box sx={fieldGrid}>
              <TextField
                label="Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
              <TextField
                label="Description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                multiline
                minRows={1}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="subtitle1">Experiments</Typography>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              startIcon={<Plus size={16} />}
              onClick={() =>
                setExperiments((current) => [...current, createExperiment()])
              }
              sx={{ borderColor: 'divider', color: 'text.primary' }}
            >
              Add experiment
            </Button>
          </Box>

          {experiments.map((experiment, index) => (
            <Box
              key={experiment.id}
              sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: { xs: 2, md: 3 },
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: 'primary.light',
                      color: 'primary.dark',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 13,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1">
                      {experiment.name.trim() || `Experiment ${index + 1}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {TYPE_LABEL[experiment.type]}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  type="button"
                  color="inherit"
                  startIcon={<Trash2 size={15} />}
                  onClick={() =>
                    setExperiments((current) =>
                      current.filter((item) => item.id !== experiment.id),
                    )
                  }
                  sx={{ color: 'text.secondary' }}
                >
                  Remove
                </Button>
              </Box>

              <Box sx={fieldGrid}>
                <TextField
                  label="Name"
                  value={experiment.name}
                  onChange={(event) =>
                    updateExperiment(experiment.id, {
                      name: event.target.value,
                    })
                  }
                />
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.75 }}
                  >
                    Type
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={experiment.type}
                    onChange={(_event, value: ExperimentType | null) => {
                      if (value) changeExperimentType(experiment.id, value);
                    }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        px: 1.75,
                        borderColor: 'divider',
                        color: 'text.secondary',
                        '&.Mui-selected': {
                          backgroundColor: '#E7F3F1',
                          color: '#0A524B',
                          '&:hover': {
                            backgroundColor: '#E7F3F1',
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="single">Single</ToggleButton>
                    <ToggleButton value="trio">Trio</ToggleButton>
                    <ToggleButton value="custom">Custom</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <TextField
                  label="Description"
                  value={experiment.description}
                  onChange={(event) =>
                    updateExperiment(experiment.id, {
                      description: event.target.value,
                    })
                  }
                />
                <TextField
                  select
                  label="Pipeline"
                  value={experiment.pipeline}
                  onChange={(event) =>
                    updateExperiment(experiment.id, {
                      pipeline: event.target.value,
                    })
                  }
                >
                  <MenuItem value="">
                    <Typography color="text.secondary">
                      Select a pipeline
                    </Typography>
                  </MenuItem>
                  {PIPELINES.map((pipeline) => (
                    <MenuItem key={pipeline} value={pipeline}>
                      {pipeline}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                  Samples
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                  {experiment.samples.map((sample) => (
                    <Box
                      key={sample.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: '1.3fr 1fr 0.8fr 1.5fr auto',
                        },
                        gap: 1.5,
                        alignItems: 'center',
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: 'background.default',
                      }}
                    >
                      <TextField
                        label="Sample ID"
                        value={sample.sampleId}
                        onChange={(event) =>
                          updateSample(experiment.id, sample.id, {
                            sampleId: event.target.value,
                          })
                        }
                      />
                      <TextField
                        select
                        label="Type"
                        value={sample.sampleType}
                        onChange={(event) =>
                          updateSample(experiment.id, sample.id, {
                            sampleType: event.target.value as SampleType,
                          })
                        }
                      >
                        <MenuItem value="bam">BAM</MenuItem>
                        <MenuItem value="unpaired_fastq">
                          Unpaired FASTQ
                        </MenuItem>
                        <MenuItem value="paired_fastq">Paired FASTQ</MenuItem>
                      </TextField>
                      <TextField
                        select
                        label="Gender"
                        value={sample.gender}
                        onChange={(event) =>
                          updateSample(experiment.id, sample.id, {
                            gender: event.target.value as Gender,
                          })
                        }
                      >
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="unknown">Unknown</MenuItem>
                      </TextField>
                      <TextField
                        label="File path"
                        value={sample.filePath}
                        placeholder="/data/sample.bam"
                        onChange={(event) =>
                          updateSample(experiment.id, sample.id, {
                            filePath: event.target.value,
                          })
                        }
                      />
                      <Button
                        type="button"
                        color="inherit"
                        aria-label="Remove sample"
                        onClick={() => removeSample(experiment.id, sample.id)}
                        disabled={experiment.samples.length === 1}
                        sx={{
                          minWidth: 40,
                          color: 'text.secondary',
                          justifySelf: { md: 'end' },
                        }}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </Box>
                  ))}
                </Box>
                <Button
                  type="button"
                  startIcon={<Plus size={16} />}
                  onClick={() => addSample(experiment.id)}
                  sx={{ mt: 1, color: 'primary.main' }}
                >
                  Add sample
                </Button>
              </Box>
            </Box>
          ))}

          {experiments.length === 0 && (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                px: 3,
                py: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle1">No experiments yet</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Add an experiment to attach a pipeline and samples.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ProjectPreview;
